import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowRightLeft, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface SwapReservationInfo {
  id: string;
  customerName: string;
  numberOfPeople: number;
  tableName: string;
  tableId: string | null;
  combinedTableId: string | null;
}

interface TableSwapConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draggedReservation: SwapReservationInfo | null;
  targetReservation: SwapReservationInfo | null;
  onConfirm: () => void;
}

export function TableSwapConfirmDialog({
  open,
  onOpenChange,
  draggedReservation,
  targetReservation,
  onConfirm,
}: TableSwapConfirmDialogProps) {
  if (!draggedReservation || !targetReservation) return null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-orange-500" />
            Échanger les tables ?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Les deux réservations vont échanger leurs tables :
              </p>
              
              <div className="space-y-3">
                {/* Dragged reservation */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{draggedReservation.customerName}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{draggedReservation.numberOfPeople} pers.</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      {draggedReservation.tableName}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      → {targetReservation.tableName}
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  <ArrowRightLeft className="h-5 w-5 text-muted-foreground rotate-90" />
                </div>

                {/* Target reservation */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{targetReservation.customerName}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{targetReservation.numberOfPeople} pers.</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      {targetReservation.tableName}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      → {draggedReservation.tableName}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className="bg-orange-500 hover:bg-orange-600"
          >
            Confirmer l'échange
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
